<script>
import api from "@/api/api"
import TicketLayouts from "@/components/Tickets/layouts/TicketLayouts.vue"
import TicketBoardCardTemplate from "@/components/Tickets/widgets/TicketBoardCardTemplate.vue"
import {mapActions, mapGetters} from "vuex"
import debounce from "lodash.debounce"
import {connectToWs, subscribe, onMessage, sendMessage, closeWs} from "@/services/websocket"
import {statusPriority} from "@/constants/ticket-status"

import {formatFileSize, isImage, getFileIcon, downloadFile} from "@/common/fileUtils"
import {getInitials} from "@/common/stringUtils"
import {mapTicketFromAPI, getActionOptionsForTicket} from "@/common/ticketMapper"
import {mergeTicketsData} from "@/common/kanbanHelpers"

/** Служебная колонка для тикетов, чей статус не попал в список колонок. */
const UNKNOWN_STATUS_SLUG = '__no_status__'
import {
  PRIORITY_OPTIONS,
  TICKETS_PER_PAGE,
  SCROLL_DEBOUNCE_MS,
  SCROLL_THRESHOLD,
  SEARCH_DEBOUNCE_MS,
  SEARCH_MAX_LENGTH,
} from "@/constants/ticketConstants"

const PRIORITY_FILTERS = [
  {value: 'all', text: 'Любой приоритет'},
  {value: 'urgent', text: 'Срочный'},
  {value: 'high', text: 'Высокий'},
  {value: 'medium', text: 'Средний'},
  {value: 'low', text: 'Низкий'},
]

export default {
  name: "TicketBoard",
  components: {TicketLayouts, TicketBoardCardTemplate},

  data() {
    return {
      statuses: [],
      kanbanData: [],
      draggedTicket: null,
      dragOverStatusId: null,
      pendingMove: null,
      isActionChoiceOpen: false,
      isMovingTicket: false,
      history: [],
      isLoading: false,
      statusPagination: {},
      loadingStatuses: {},
      scrollDebounceTimers: {},

      search: '',
      /** Строка, с которой реально ушёл запрос: поиск по ID и теме делает бэк. */
      searchQuery: '',
      searchMaxLength: SEARCH_MAX_LENGTH,
      priorityFilter: 'all',
      priorityFilters: PRIORITY_FILTERS,

      payload: {
        id: null,
        title: '',
        description: '',
        priority: '',
        action_id: null,
        category: {id: null, name: ''},
        type: {id: null, name: ''},
      },
      isSidebarLoading: false,
      isHistoryLoading: false,
      isMessagesLoading: false,
      isHistoryTab: false,
      sidebarTicket: null,
      priorityOptions: PRIORITY_OPTIONS,

      messages: [],
      newMessageText: '',
      isSendingMessage: false,
      attachedFiles: [],
      token: localStorage.getItem('token'),
    }
  },

  computed: {
    ...mapGetters('moduleTickets', ['ticketActions', 'categoryOptions', 'typeOptions']),
    ...mapGetters(['getUserId', 'getUserName', 'getUserEmail']),

    userId() {
      return this.getUserId
    },
    userName() {
      return this.getUserName
    },
    userEmail() {
      return this.getUserEmail
    },

    sortedStatuses() {
      const statuses = Array.isArray(this.statuses) ? this.statuses : []
      return statuses.slice().sort((a, b) => {
        const aPriority = statusPriority[a.slug] ?? Number.MAX_SAFE_INTEGER
        const bPriority = statusPriority[b.slug] ?? Number.MAX_SAFE_INTEGER
        return aPriority - bPriority
      })
    },

    hasSearch() {
      return Boolean(this.searchQuery)
    },

    /** Приоритет бэк не фильтрует — он остаётся фильтром по загруженным карточкам. */
    hasClientFilter() {
      return this.priorityFilter !== 'all'
    },

    hasAnyFilter() {
      return this.hasSearch || this.hasClientFilter
    },

    filteredTickets() {
      if (!this.hasClientFilter) return this.kanbanData || []
      return (this.kanbanData || []).filter(ticket => ticket.Priority === this.priorityFilter)
    },

    ticketsByStatus() {
      const grouped = {}
      this.sortedStatuses.forEach(status => {
        grouped[status.slug] = []
      })
      grouped[UNKNOWN_STATUS_SLUG] = []

      // второй барьер от дублей: один Id — одна карточка на доске, откуда бы
      // тикет ни пришёл (колонки, вебсокет, локальные патчи)
      const seen = new Set()

      this.filteredTickets.forEach(ticket => {
        const id = ticket.Id != null ? String(ticket.Id) : null
        if (id !== null) {
          if (seen.has(id)) return
          seen.add(id)
        }

        // тикет без action.status или со статусом вне списка колонок раньше
        // попадал в осиротевшую группу, которую никто не рисовал: поиск
        // «не находил» тикет, хотя он был загружен
        const slug = grouped[ticket.Status] ? ticket.Status : UNKNOWN_STATUS_SLUG
        grouped[slug].push(ticket)
      })
      return grouped
    },

    /** Колонки доски плюс служебная «Без статуса», если такие тикеты есть. */
    boardColumns() {
      const orphans = this.ticketsByStatus[UNKNOWN_STATUS_SLUG] || []
      if (!orphans.length) return this.sortedStatuses
      return [...this.sortedStatuses, {id: UNKNOWN_STATUS_SLUG, slug: UNKNOWN_STATUS_SLUG, name: 'Без статуса'}]
    },

    visibleTotal() {
      return this.filteredTickets.length
    },

    loadedTotal() {
      return (this.kanbanData || []).length
    },

    /** Сколько тикетов нашёл бэк по всем статусам: meta.total каждой колонки. */
    foundTotal() {
      return Object.values(this.statusPagination).reduce((sum, item) => sum + (item.total || 0), 0)
    },

    filterChipText() {
      if (this.hasSearch && this.hasClientFilter) {
        return `Показано ${this.visibleTotal} из ${this.foundTotal} найденных`
      }
      if (this.hasSearch) return `Найдено ${this.foundTotal}`
      return `Показано ${this.visibleTotal} из ${this.loadedTotal} загруженных`
    },

    emptyColumnText() {
      if (this.hasSearch) return 'Ничего не нашлось'
      if (this.hasClientFilter) return 'Под фильтр ничего не попало'
      return 'Тикетов нет'
    },

    actionOptions() {
      return getActionOptionsForTicket(this.payload.action_id, this.ticketActions)
    },

    reversedMessages() {
      return this.messages.slice().reverse().map(msg => ({
        ...msg,
        initials: this.getInitials(msg.user && msg.user.username || 'U'),
        isMine: this.isMyMessage(msg)
      }))
    }
  },

  methods: {
    getInitials,
    formatFileSize,
    isImage,
    getFileIcon,
    ...mapActions('moduleTickets', ['getAllTicketActions', 'getTicketCategories', 'getTicketTypes']),

    // ===== Колонки =====
    columnCount(status) {
      const pagination = this.statusPagination[status.id]
      // при поиске meta.total приходит уже по найденным — врёт только фильтр приоритета
      if (this.hasClientFilter) return (this.ticketsByStatus[status.slug] || []).length
      return pagination ? pagination.total : (this.ticketsByStatus[status.slug] || []).length
    },

    columnHasMore(status) {
      const pagination = this.statusPagination[status.id]
      return Boolean(pagination && pagination.current_page < pagination.last_page)
    },

    /**
     * Догрузка следующей страницы при подходе к низу колонки.
     * Раньше обработчики вешались на внутренний DOM Syncfusion по таймерам —
     * теперь колонка своя, и скролл приходит обычным событием.
     */
    onColumnScroll(event, status) {
      const element = event.target
      if (this.scrollDebounceTimers[status.id]) clearTimeout(this.scrollDebounceTimers[status.id])

      this.scrollDebounceTimers[status.id] = setTimeout(() => {
        const {scrollTop, scrollHeight, clientHeight} = element
        if (scrollHeight <= clientHeight) return
        if ((scrollTop + clientHeight) / scrollHeight >= SCROLL_THRESHOLD) {
          this.loadMoreTicketsForStatus(status.id)
        }
      }, SCROLL_DEBOUNCE_MS)
    },

    async loadMoreTicketsForStatus(statusId) {
      const pagination = this.statusPagination[statusId]
      if (!pagination) return
      if (this.loadingStatuses[statusId]) return
      if (pagination.current_page >= pagination.last_page) return

      await this.loadTicketsForStatus(statusId, pagination.current_page + 1, false)
    },

    async getListTickets() {
      this.isLoading = true
      try {
        await this.getStatuses()
        await Promise.all(this.statuses.map(status => this.loadTicketsForStatus(status.id, 1, true)))
      } finally {
        this.isLoading = false
      }
    },

    async loadTicketsForStatus(statusId, page = 1, isInitial = false) {
      if (!isInitial) this.$set(this.loadingStatuses, statusId, true)

      try {
        const params = {status: statusId, page, per_page: TICKETS_PER_PAGE}
        if (this.searchQuery) params.search = this.searchQuery

        const res = await api.tickets.getListTickets(params)

        const tickets = (res.data?.data || []).map(ticket => mapTicketFromAPI(ticket, this.ticketActions))
        const currentStatusSlug = this.statuses.find(status => status.id === statusId)?.slug

        this.kanbanData = mergeTicketsData(this.kanbanData, tickets, currentStatusSlug, page === 1)

        this.$set(this.statusPagination, statusId, {
          current_page: res.data?.meta?.current_page || page,
          last_page: res.data?.meta?.last_page || 1,
          per_page: res.data?.meta?.per_page || TICKETS_PER_PAGE,
          total: res.data?.meta?.total || 0,
        })
      } catch (e) {
        this.$toast.error('Не удалось загрузить тикеты')
      } finally {
        if (!isInitial) this.$set(this.loadingStatuses, statusId, false)
      }
    },

    async getStatuses() {
      try {
        const res = await api.tickets.getListActionStatuses()
        this.statuses = res.data?.data || []
      } catch (e) {
        this.statuses = []
        this.$toast.error('Не удалось загрузить статусы')
      }
    },

    /** Перезагрузка колонок с первой страницы — статусы уже известны. */
    async reloadColumns() {
      if (!this.statuses.length) return this.getListTickets()

      this.isLoading = true
      try {
        await Promise.all(this.statuses.map(status => this.loadTicketsForStatus(status.id, 1, true)))
      } finally {
        this.isLoading = false
      }
    },

    async applySearch() {
      const query = this.search.trim().slice(0, SEARCH_MAX_LENGTH)
      if (query === this.searchQuery) return

      this.searchQuery = query
      // страницы колонок считаются от новой выборки, поэтому грузим заново с первой
      this.statusPagination = {}
      this.kanbanData = []
      await this.reloadColumns()
    },

    /** Enter — не ждём дебаунс. */
    applySearchNow() {
      this.debouncedSearch.cancel()
      this.applySearch()
    },

    clearSearch() {
      this.search = ''
      this.debouncedSearch.cancel()
      this.applySearch()
    },

    resetFilters() {
      this.priorityFilter = 'all'
      this.clearSearch()
    },

    // ===== Сайдбар =====
    async openSidebar(ticketData, mode = 'chat') {
      this.sidebarTicket = ticketData
      this.isHistoryTab = mode === 'history'

      if (ticketData) {
        this.payload = {
          id: ticketData.Id,
          title: ticketData.Title,
          description: ticketData.Summary,
          priority: ticketData.Priority,
          action_id: ticketData.ActionId,
          category: {id: ticketData.CategoryId, name: ticketData.Category},
          type: {id: ticketData.TypeId, name: ticketData.Type},
        }
      }

      this.$root.$emit('bv::toggle::collapse', 'sidebar-right')

      if (!ticketData?.Id) return

      if (mode === 'history') {
        await this.getTicketHistory(ticketData.Id)
      } else {
        this.subscribeToTicketChat(ticketData.Id)
        await this.getTicketMessages(ticketData.Id)
        this.$nextTick(() => this.scrollToBottom())
        const markReadMessageStatus = (await api.tickets.markMessageAsRead(ticketData.Id)).status
        if (markReadMessageStatus === 204) {
          this.patchTicket(ticketData.Id, {UnreadMessageCount: 0})
        }
      }
    },

    handleSidebarClose() {
      this.sidebarTicket = null
      this.isHistoryTab = false
      document.body.style.overflow = 'auto'
    },

    /** Точечное обновление карточки в списке — без перезагрузки всей доски. */
    patchTicket(ticketId, patch) {
      this.kanbanData = this.kanbanData.map(ticket =>
          ticket.Id === ticketId ? {...ticket, ...patch} : ticket
      )
    },

    async onSelectTicketPriority() {
      this.isSidebarLoading = true
      try {
        const {id, priority} = this.payload
        const res = await api.tickets.updateTicket(id, {priority})
        if (res.status === 204) this.patchTicket(id, {Priority: priority})
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
      } finally {
        this.isSidebarLoading = false
      }
    },

    async onSelectTicketCategory() {
      this.isSidebarLoading = true
      try {
        const {id, category} = this.payload
        const res = await api.tickets.updateTicket(id, {category_id: category.id})
        if (res.status === 204) {
          const categoryName = this.categoryOptions.find(item => item.value === category.id)?.text
          this.patchTicket(id, {Category: categoryName, CategoryId: category.id})
        }
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
      } finally {
        this.isSidebarLoading = false
      }
    },

    async onSelectTicketType() {
      this.isSidebarLoading = true
      try {
        const {id, type} = this.payload
        const res = await api.tickets.updateTicket(id, {type_id: type.id})
        if (res.status === 204) {
          const typeName = this.typeOptions.find(item => item.value === type.id)?.text
          this.patchTicket(id, {Type: typeName, TypeId: type.id})
        }
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
      } finally {
        this.isSidebarLoading = false
      }
    },

    /* ======================= перенос карточек ======================= */

    /** Статусы приходят слагами (in_progress), у действий — названиями (In Progress). */
    normalizeStatus(value) {
      return String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
    },

    /**
     * Автоскролл доски во время перетаскивания.
     *
     * HTML5 drag&drop сам страницу не прокручивает: чтобы дотащить карточку из
     * «New» в «Rejected», приходилось уменьшать масштаб браузера. Пока карточку
     * тащат у края доски — двигаем её по горизонтали, у края колонки — по
     * вертикали.
     */
    startBoardAutoScroll() {
      if (this._autoScroll) return;

      this._autoScroll = {x: 0, y: 0, column: null, raf: null};

      this._autoScrollTrack = (event) => {
        const board = this.$refs.board;
        if (!board) return;

        const EDGE = 90;          // ширина чувствительной зоны у края
        const MAX_SPEED = 22;     // пикселей за кадр у самого края

        const speed = (distance) => {
          if (distance >= EDGE) return 0;
          return Math.ceil(MAX_SPEED * (1 - Math.max(distance, 0) / EDGE));
        };

        const rect = board.getBoundingClientRect();
        const {clientX, clientY} = event;

        this._autoScroll.x = speed(clientX - rect.left) * -1 || speed(rect.right - clientX);

        // вертикаль — у колонки, над которой сейчас курсор
        const under = typeof document.elementFromPoint === 'function'
            ? document.elementFromPoint(clientX, clientY)
            : null;
        const column = under && under.closest('.tb-col__body');
        this._autoScroll.column = column || null;
        if (column) {
          const colRect = column.getBoundingClientRect();
          this._autoScroll.y = speed(clientY - colRect.top) * -1 || speed(colRect.bottom - clientY);
        } else {
          this._autoScroll.y = 0;
        }
      };

      const step = () => {
        if (!this._autoScroll) return;
        const board = this.$refs.board;
        const {x, y, column} = this._autoScroll;

        if (board && x) board.scrollLeft += x;
        if (column && y) column.scrollTop += y;

        this._autoScroll.raf = requestAnimationFrame(step);
      };

      document.addEventListener('dragover', this._autoScrollTrack);
      this._autoScroll.raf = requestAnimationFrame(step);
    },

    stopBoardAutoScroll() {
      if (!this._autoScroll) return;
      cancelAnimationFrame(this._autoScroll.raf);
      document.removeEventListener('dragover', this._autoScrollTrack);
      this._autoScroll = null;
      this._autoScrollTrack = null;
    },

    /**
     * Статус, в котором тикет окажется после действия.
     *
     * Раньше карточка уезжала в ту колонку, куда её бросили. Но колонка — это
     * намерение админа, а не результат: «Эскалировать» из New ведёт обратно в
     * New со сменой линии, «Переоткрыт» из In Progress(2) — в New(1).
     * Поэтому целевой статус берём у самого действия: сначала по status_id
     * (точное совпадение), и только потом по имени — как запасной путь.
     */
    statusForAction(action) {
      if (!action) return null;

      if (action.status_id != null) {
        const byId = (this.statuses || []).find(status => status.id === action.status_id);
        if (byId) return byId;
      }

      return this.statusBySlugOrName(action.status);
    },

    /** Действия, после которых тикет окажется в этой колонке. */
    actionsForStatus(ticket, status) {
      if (!ticket || !status) return [];

      const allowedIds = (ticket.ActionOptions || [])
          .map(option => option.value)
          .filter(id => id !== ticket.ActionId);

      return (this.ticketActions || [])
          .filter(action => allowedIds.includes(action.id))
          .filter(action => {
            const resulting = this.statusForAction(action);
            return resulting && resulting.id === status.id;
          });
    },

    onCardDragStart(ticket) {
      this.draggedTicket = ticket;
      this.startBoardAutoScroll();
    },

    onCardDragEnd() {
      this.draggedTicket = null;
      this.dragOverStatusId = null;
      this.stopBoardAutoScroll();
    },

    canDropTo(status) {
      if (!this.draggedTicket || this.isMovingTicket) return false;
      // переход внутри одной колонки — это смена линии (New(1) → New(2),
      // In Progress(2) → In Progress(1)); раньше такой дроп был запрещён
      return this.actionsForStatus(this.draggedTicket, status).length > 0;
    },

    columnDropClass(status) {
      if (!this.draggedTicket) return '';
      if (!this.canDropTo(status)) return 'is-drop-forbidden';
      return this.dragOverStatusId === status.id ? 'is-drop-over' : 'is-drop-allowed';
    },

    onColumnDragOver(event, status) {
      if (!this.canDropTo(status)) return;
      // без preventDefault браузер не считает область зоной сброса
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      this.dragOverStatusId = status.id;
    },

    onColumnDragLeave(status) {
      if (this.dragOverStatusId === status.id) this.dragOverStatusId = null;
    },

    onColumnDrop(status) {
      const ticket = this.draggedTicket;
      this.dragOverStatusId = null;
      this.stopBoardAutoScroll();
      if (!ticket) return;

      const actions = this.actionsForStatus(ticket, status);
      if (!actions.length) return;

      // одно действие — переносим сразу, несколько — спрашиваем какое
      if (actions.length === 1) {
        this.applyMove(ticket, status, actions[0]);
        return;
      }

      this.pendingMove = {ticket, status, actions};
      this.isActionChoiceOpen = true;
    },

    confirmMove(action) {
      if (!this.pendingMove) return;
      const {ticket, status} = this.pendingMove;
      this.isActionChoiceOpen = false;
      this.applyMove(ticket, status, action);
    },

    /** Действия, доступные из нового действия тикета — как их считает mapTicketFromAPI. */
    nextActionOptions(actionId) {
      return (this.ticketActions || [])
          .filter(action => action.parent_action_id === actionId)
          .map(action => ({text: action.title || action.name, value: action.id}))
    },

    statusBySlugOrName(value) {
      const target = this.normalizeStatus(value)
      return (this.statuses || []).find(status => {
        return this.normalizeStatus(status.slug) === target || this.normalizeStatus(status.name) === target
      }) || null
    },

    /** Счётчик колонки правим локально, чтобы не перезапрашивать всю доску. */
    shiftColumnTotal(status, delta) {
      if (!status) return
      const pagination = this.statusPagination[status.id]
      if (!pagination || typeof pagination.total !== 'number') return
      this.$set(this.statusPagination, status.id, {
        ...pagination,
        total: Math.max(pagination.total + delta, 0),
      })
    },

    /** Переносит карточку в другую колонку без перезагрузки доски. */
    moveTicketLocally(ticket, action, targetStatus) {
      const fromStatus = this.statusBySlugOrName(ticket.Status)
      const snapshot = {
        Status: ticket.Status,
        ActionId: ticket.ActionId,
        action: ticket.action,
        ActionOptions: ticket.ActionOptions,
        fromStatus,
        targetStatus,
      }

      this.patchTicket(ticket.Id, {
        Status: targetStatus ? targetStatus.slug : ticket.Status,
        ActionId: action.id,
        action,
        ActionOptions: this.nextActionOptions(action.id),
      })

      if (targetStatus && fromStatus && targetStatus.id !== fromStatus.id) {
        this.shiftColumnTotal(fromStatus, -1)
        this.shiftColumnTotal(targetStatus, 1)
      }

      return snapshot
    },

    restoreTicketLocally(ticketId, snapshot) {
      this.patchTicket(ticketId, {
        Status: snapshot.Status,
        ActionId: snapshot.ActionId,
        action: snapshot.action,
        ActionOptions: snapshot.ActionOptions,
      })

      if (snapshot.targetStatus && snapshot.fromStatus && snapshot.targetStatus.id !== snapshot.fromStatus.id) {
        this.shiftColumnTotal(snapshot.targetStatus, -1)
        this.shiftColumnTotal(snapshot.fromStatus, 1)
      }
    },

    /**
     * Приводит карточку к тому, что реально записал бэкенд.
     *
     * Локальный перенос — только оптимистичная догадка по графу действий.
     * Бэкенд может решить иначе (эскалация меняет линию, «Решён» со 2 линии
     * возвращает тикет на 1), и до этой синхронизации расхождение было видно
     * только после перезагрузки страницы. Один GET вместо перезагрузки доски.
     */
    async syncTicketFromServer(ticketId, fallbackStatus) {
      try {
        const res = await api.tickets.getTicket(ticketId)
        const raw = res.data?.data || res.data
        if (!raw || !raw.id) return fallbackStatus

        const fresh = mapTicketFromAPI(raw, this.ticketActions)
        const previous = (this.kanbanData || []).find(item => item.Id === ticketId)
        const actualStatus = this.statusBySlugOrName(fresh.Status)

        // счётчики колонок правим, если бэкенд увёл тикет не туда, куда мы ждали
        if (previous && previous.Status !== fresh.Status) {
          this.shiftColumnTotal(this.statusBySlugOrName(previous.Status), -1)
          this.shiftColumnTotal(actualStatus, 1)
        }

        this.patchTicket(ticketId, {
          Status: fresh.Status,
          ActionId: fresh.ActionId,
          action: fresh.action,
          ActionOptions: fresh.ActionOptions,
          ParentActionId: fresh.ParentActionId,
        })

        // селектор в сайдбаре и карточка на доске должны показывать одно и то же
        if (this.sidebarTicket && this.sidebarTicket.Id === ticketId) {
          this.sidebarTicket = {...this.sidebarTicket, ...fresh}
          this.payload = {...this.payload, action_id: fresh.ActionId}
        }

        return actualStatus || fallbackStatus
      } catch (e) {
        // синхронизация — подстраховка: её падение не должно рушить перенос
        return fallbackStatus
      }
    },

    async applyMove(ticket, status, action) {
      this.isMovingTicket = true
      // в какую колонку тикет попадёт, решает действие, а не место дропа
      const targetStatus = this.statusForAction(action) || status
      const snapshot = this.moveTicketLocally(ticket, action, targetStatus)

      try {
        await api.tickets.updateActionFromTicket(ticket.Id, action.id, {action_id: action.id})
        const finalStatus = await this.syncTicketFromServer(ticket.Id, targetStatus)
        this.$toast.success(`Тикет #${ticket.Id} → ${(finalStatus && finalStatus.name) || targetStatus.name}`)
      } catch (e) {
        this.restoreTicketLocally(ticket.Id, snapshot)
        this.$toast.error('Не удалось перенести тикет')
      } finally {
        this.isMovingTicket = false
        this.pendingMove = null
        this.draggedTicket = null
      }
    },

    async onSelectTicketAction() {
      const {id, action_id} = this.payload
      const ticket = (this.kanbanData || []).find(item => item.Id === id)
      const action = (this.ticketActions || []).find(item => item.id === action_id)

      // карточку двигаем локально; если действие не нашли — остаётся запасной путь с перезагрузкой
      const snapshot = (ticket && action)
          ? this.moveTicketLocally(ticket, action, this.statusForAction(action))
          : null

      try {
        await api.tickets.updateActionFromTicket(id, action_id, {action_id})
        if (snapshot) {
          // смена действия из деталей тикета обязана двигать карточку на доске
          await this.syncTicketFromServer(id, snapshot.targetStatus)
        } else {
          await this.getListTickets()
        }
      } catch (e) {
        if (snapshot) this.restoreTicketLocally(id, snapshot)
        this.payload = {...this.payload, action_id: snapshot ? snapshot.ActionId : this.payload.action_id}
        this.$toast.error('Ошибка при обновлении тикета')
      }
    },

    async updateTicket() {
      this.isSidebarLoading = true
      try {
        const {id, ...updateData} = this.payload
        await api.tickets.updateTicket(id, updateData)
        this.patchTicket(id, {Title: updateData.title, Summary: updateData.description})
        this.$toast.success('Тикет обновлён')
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
      } finally {
        this.isSidebarLoading = false
      }
    },

    async getTicketHistory(ticketId) {
      // важно: грузим со своим флагом, иначе весь сайдбар подменялся спиннером
      // и при переходе на вкладку «История» выглядел как полная перезагрузка
      try {
        this.isHistoryLoading = true
        const res = await api.tickets.getTicketHistory(ticketId)
        this.history = res.data.data
      } catch (e) {
        this.$toast.error('Ошибка при получении истории тикета')
      } finally {
        this.isHistoryLoading = false
      }
    },

    async getTicketMessages(ticketId) {
      try {
        this.isMessagesLoading = true
        this.attachedFiles = []
        const res = await api.tickets.getListOfMessages(ticketId)
        this.messages = res.data?.data || []
        this.$nextTick(() => this.scrollToBottom())
      } catch (e) {
        this.$toast.error('Ошибка при получении сообщений')
      } finally {
        this.isMessagesLoading = false
      }
    },

    // ===== Чат =====
    async handleSendMessage() {
      if (!this.newMessageText.trim() && !this.attachedFiles.length) {
        this.$toast.warning('Введите сообщение или прикрепите файл')
        return
      }
      if (this.isSendingMessage) return
      this.isSendingMessage = true

      try {
        const formData = new FormData()
        formData.append('message', this.newMessageText)
        this.attachedFiles.forEach((file) => formData.append('attachments[]', file.raw))

        const res = await api.tickets.sendMessage(this.sidebarTicket.Id, formData)

        this.messages.unshift(res.data.data)
        sendMessage({
          channel: `ticket.${this.sidebarTicket.Id}.messages`,
          event: 'ticket.message.sent',
          data: {
            ticket_id: res.data.data.ticket_id,
            message: res.data.data.message,
            id: res.data.data.id,
            user_id: res.data.data.user_id,
            user: res.data.data.user,
            attachments: res.data.data.attachments,
            created_at: res.data.data.created_at,
          }
        })
        this.newMessageText = ''
        this.attachedFiles = []
        this.$nextTick(() => this.scrollToBottom())
      } catch (e) {
        this.$toast.error('Ошибка отправки сообщения')
      } finally {
        this.isSendingMessage = false
      }
    },

    scrollToBottom() {
      const chatMessages = this.$refs.chatMessages
      if (chatMessages) chatMessages.scrollTop = chatMessages.scrollHeight
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      files.forEach(file => {
        this.attachedFiles.push({
          raw: file,
          name: file.name,
          size: file.size,
          preview: isImage(file.name) ? URL.createObjectURL(file) : null,
        })
      })
      this.$refs.fileInput.value = ''
    },

    removeAttachedFile(index) {
      const file = this.attachedFiles[index]
      if (file.preview) URL.revokeObjectURL(file.preview)
      this.attachedFiles.splice(index, 1)
    },

    isMyMessage(msg) {
      return msg.user?.username === this.userName || msg.user?.email === this.userEmail
    },

    downloadFile(url, filename) {
      downloadFile(url, filename, ({title, message, variant}) => {
        this.$bvToast.toast(message, {title, variant, solid: true})
      })
    },

    // ===== WebSocket =====
    handleMessage(data) {
      switch (data.type || data.event) {
        case 'ticket.message.sent':
          this.handleNewMessage(data)
          break
        case 'ticket_updated':
          this.getListTickets()
          break
      }
    },

    handleNewMessage(data) {
      const message = data.data?.ticket_message || data.data
      if (this.isHistoryTab || !message || this.sidebarTicket?.Id !== message.ticket_id) return

      if (!this.messages.some(item => item.id === message.id)) {
        this.messages.unshift(message)
        this.$nextTick(() => this.scrollToBottom())
      }
    },

    subscribeToTicketChat(ticketId) {
      subscribe(`ticket.${ticketId}.messages`, this.token)
    },

    // ===== Прочее =====
    copyTicketId(id) {
      const text = String(id)
      const done = () => this.$toast.success(`ID ${id} скопирован`)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
        })
        return
      }
      const area = document.createElement('textarea')
      area.value = text
      document.body.appendChild(area)
      area.select()
      try {
        document.execCommand('copy')
        done()
      } finally {
        document.body.removeChild(area)
      }
    },

    navigateToUser(userId) {
      if (!userId) return
      const route = this.$router.resolve(`/users/${userId}`)
      window.open(route.href, '_blank')
    },

    formatChatDate(value) {
      if (!value) return ''
      const date = new Date(value)
      return isNaN(date.getTime()) ? '' : date.toLocaleString('ru-RU')
    },
  },

  created() {
    this.debouncedSearch = debounce(this.applySearch, SEARCH_DEBOUNCE_MS)
  },

  async mounted() {
    // справочник действий обязан приехать до тикетов: mapTicketFromAPI считает
    // по нему ActionOptions, и при гонке карточки оставались без доступных
    // переходов — перетаскивание молча не работало до перезагрузки страницы
    await this.getAllTicketActions()

    this.getListTickets()
    this.getTicketCategories()
    this.getTicketTypes()

    connectToWs()
    onMessage(this.handleMessage)
    subscribe(`tickets.${1}`, this.token)
    subscribe(`users.${this.userId}.tickets`, this.token)
    subscribe(`admin-users.${this.userId}.tickets`, this.token)
  },

  beforeDestroy() {
    this.stopBoardAutoScroll()
    closeWs()
    this.debouncedSearch.cancel()
    Object.values(this.scrollDebounceTimers).forEach(timer => clearTimeout(timer))
    this.attachedFiles.forEach(file => {
      if (file.preview) URL.revokeObjectURL(file.preview)
    })
  },

  watch: {
    search() {
      this.debouncedSearch()
    },
    sidebarTicket: {
      handler(newVal) {
        document.body.style.overflow = newVal?.Id ? 'hidden' : 'auto'
        const sidebarBody = document.querySelector('.b-sidebar-body')
        if (sidebarBody) sidebarBody.style.overflow = newVal?.Id ? 'hidden' : 'auto'
      },
      deep: true
    },
    isHistoryTab: {
      async handler(newVal) {
        if (newVal === true && this.sidebarTicket?.Id) {
          await this.getTicketHistory(this.sidebarTicket?.Id)
        }
      }
    },
  }
}
</script>

<template>
  <TicketLayouts
      :sidebar-loading="isSidebarLoading"
      sidebar-mode="custom"
      :title="sidebarTicket ? `Тикет #${sidebarTicket.Id}` : 'Тикет'"
      @sidebar-close="handleSidebarClose"
  >
    <!-- ======================= САЙДБАР ТИКЕТА ======================= -->
    <template #sidebar-custom-content>
      <div class="sidebar-content-wrapper">
        <div class="sidebar-top-section">
          <b-form @submit.prevent="updateTicket">
            <label class="tb-label">Тема</label>
            <b-form-input v-model="payload.title" placeholder="Тема обращения"/>

            <label class="tb-label mt-2">Описание</label>
            <b-form-input v-model="payload.description" placeholder="Короткое описание"/>

            <b-button class="mt-2" size="sm" variant="primary" type="submit">
              Сохранить тему и описание
            </b-button>
          </b-form>

          <div class="tb-side-grid">
            <b-form-group label="Приоритет" label-for="ticket-priority">
              <b-form-select id="ticket-priority" v-model="payload.priority"
                             :options="priorityOptions" @change="onSelectTicketPriority"/>
            </b-form-group>
            <b-form-group label="Действие" label-for="ticket-action">
              <b-form-select id="ticket-action" v-model="payload.action_id"
                             :options="actionOptions" @change="onSelectTicketAction"/>
            </b-form-group>
            <b-form-group label="Категория" label-for="ticket-category">
              <b-form-select id="ticket-category" v-model="payload.category.id"
                             :options="categoryOptions" @change="onSelectTicketCategory"/>
            </b-form-group>
            <b-form-group label="Тип" label-for="ticket-type">
              <b-form-select id="ticket-type" v-model="payload.type.id"
                             :options="typeOptions" @change="onSelectTicketType"/>
            </b-form-group>
          </div>

          <div class="tabs w-100 d-flex justify-content-between align-items-center mt-3">
            <b-button @click="isHistoryTab = false" class="w-100 border-none rounded-0"
                      :class="{'active': !isHistoryTab}">
              Чат
            </b-button>
            <b-button @click="isHistoryTab = true" class="w-100 border-none rounded-0"
                      :class="{'active': isHistoryTab}">
              История
            </b-button>
          </div>
        </div>

        <!-- История -->
        <div v-if="isHistoryTab" class="sidebar-scroll-section">
          <div v-if="isHistoryLoading" class="tb-side-loading">
            <div v-for="n in 4" :key="'hsk' + n" class="tb-side-loading__row">
              <span class="tb-sk tb-sk--dot"></span>
              <span class="tb-sk tb-sk--line"></span>
            </div>
          </div>
          <div class="history-list mt-3" v-else-if="history.length">
            <div class="history-item" v-for="(item, index) in history" :key="'h' + index">
              <div class="history-icon">
                <i class="fa fa-arrow-circle-right"></i>
              </div>
              <div class="history-content">
                <div class="history-action">Действие <span>{{ (item.action && item.action.title) || '—' }}</span></div>
                <div class="history-action">Статус <span>{{ (item.action && item.action.status && item.action.status.name) || '—' }}</span></div>
                <div class="history-meta">
                  <span class="history-user">
                    {{ (item.action && item.action.group && item.action.group.group_name) || '—' }} | {{ (item.created_by && item.created_by.username) || 'Unknown' }}
                  </span>
                  <span class="history-date">{{ formatChatDate(item.created_at) }}</span>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="tb-side-empty">История пуста</p>
        </div>

        <!-- Чат -->
        <template v-else>
          <div class="chat-messages-section">
            <div v-if="isMessagesLoading" class="chat-messages">
              <div class="tb-side-loading">
                <div v-for="n in 4" :key="'msk' + n" class="tb-side-loading__row">
                  <span class="tb-sk tb-sk--dot"></span>
                  <span class="tb-sk tb-sk--line"></span>
                </div>
              </div>
            </div>
            <div class="chat-messages" ref="chatMessages" v-else-if="reversedMessages.length">
              <div class="message" :class="{ 'message-mine': msg.isMine }"
                   v-for="(msg, index) in reversedMessages" :key="'m' + index">
                <div class="message-avatar" @click="navigateToUser(msg.user && msg.user.id)">
                  {{ msg.initials }}
                </div>
                <div class="message-content">
                  <div class="message-header">
                    <span class="message-author" @click="navigateToUser(msg.user && msg.user.id)">
                      {{ msg.user && msg.user.username || 'Unknown' }}
                    </span>
                    <span class="message-time">{{ formatChatDate(msg.created_at) }}</span>
                  </div>
                  <div class="message-text">{{ msg.message }}</div>

                  <div v-if="msg.attachments && msg.attachments.length" class="message-attachments">
                    <div v-for="(attachment, attIndex) in msg.attachments" :key="'a' + attIndex"
                         class="attachment-item">
                      <div v-if="isImage(attachment.name)" class="attachment-image-wrapper">
                        <img :src="attachment.url" :alt="attachment.name" class="attachment-image"
                             width="200" height="200"
                             @click="downloadFile(attachment.url, attachment.name)"/>
                        <div class="attachment-image-info">
                          <span class="attachment-name">{{ attachment.name }}</span>
                          <span class="attachment-size">{{ formatFileSize(attachment.size) }}</span>
                        </div>
                        <b-button size="sm" variant="light" class="attachment-download-btn"
                                  @click="downloadFile(attachment.url, attachment.name)">
                          <i class="fa fa-download"></i>
                        </b-button>
                      </div>

                      <div v-else class="attachment-file" @click="downloadFile(attachment.url, attachment.name)">
                        <div class="attachment-icon"><i class="fa fa-file-o"></i></div>
                        <div class="attachment-info">
                          <div class="attachment-name">{{ attachment.name }}</div>
                          <div class="attachment-size">{{ formatFileSize(attachment.size) }}</div>
                        </div>
                        <div class="attachment-download"><i class="fa fa-download"></i></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="chat-messages" ref="chatMessages">
              <p class="tb-side-empty">Сообщений пока нет</p>
            </div>
          </div>

          <div class="chat-input-wrapper">
            <div v-if="attachedFiles.length" class="attached-files-preview">
              <div v-for="(file, index) in attachedFiles" :key="'f' + index" class="attached-file-item">
                <img v-if="isImage(file.name)" :src="file.preview" class="attached-file-thumb" alt="preview"/>
                <i v-else class="fa fa-file-o attached-file-icon"></i>
                <span class="attached-file-name" :title="file.name">{{ file.name }}</span>
                <span class="attached-file-size">{{ formatFileSize(file.size) }}</span>
                <b-button variant="link" size="sm" class="attached-file-remove"
                          @click="removeAttachedFile(index)">
                  <i class="fa fa-times"></i>
                </b-button>
              </div>
            </div>

            <div class="chat-input">
              <b-form-textarea v-model="newMessageText" placeholder="Введите сообщение… (Ctrl+Enter — отправить)"
                               rows="1" class="mb-2 w-100" @keydown.ctrl.enter="handleSendMessage"/>
              <input type="file" ref="fileInput" multiple
                     accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                     style="display: none" @change="handleFileSelect"/>
              <b-button variant="outline-secondary" size="sm"
                        class="d-flex justify-content-center align-items-center attach-btn"
                        @click="$refs.fileInput.click()">
                <i class="fa fa-paperclip"></i>
              </b-button>
              <b-button variant="primary" size="sm" block
                        class="d-flex justify-content-center align-items-center"
                        @click="handleSendMessage"
                        :disabled="(!newMessageText.trim() && !attachedFiles.length) || isSendingMessage">
                <i class="fa" :class="isSendingMessage ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
              </b-button>
            </div>
          </div>
        </template>
      </div>
    </template>

    <!-- выбор действия: в один статус может вести несколько действий -->
    <b-modal v-model="isActionChoiceOpen" centered hide-footer
             :title="`Тикет #${(pendingMove && pendingMove.ticket.Id) || ''} → ${(pendingMove && pendingMove.status.name) || ''}`">
      <p class="tb-choice__text">Выберите действие, которым переводим тикет:</p>
      <div class="tb-choice">
        <button v-for="action in (pendingMove ? pendingMove.actions : [])" :key="'act' + action.id"
                type="button" class="tb-choice__btn"
                :disabled="isMovingTicket"
                @click="confirmMove(action)">
          <i class="fa fa-bolt"></i>
          <span>{{ action.title || action.name }}</span>
        </button>
      </div>
    </b-modal>

    <!-- ======================= ДОСКА ======================= -->
    <div class="tb">
      <div class="tb-toolbar">
        <div class="tb-search">
          <i class="fa fa-search tb-search__icon"></i>
          <input ref="search" type="search" class="tb-search__input" v-model="search"
                 :maxlength="searchMaxLength" placeholder="ID или тема обращения…"
                 @keydown.esc.prevent="clearSearch" @keydown.enter.prevent="applySearchNow"/>
          <button v-if="search" type="button" class="tb-search__clear" @click="clearSearch">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <b-dropdown variant="link" class="tb-drop" no-caret menu-class="tb-drop__menu">
          <template #button-content>
            <span class="tb-drop__btn" :class="{ 'is-on': priorityFilter !== 'all' }">
              <i class="fa fa-flag-o"></i>
              <span>{{ (priorityFilters.find(item => item.value === priorityFilter) || {}).text }}</span>
              <i class="fa fa-angle-down"></i>
            </span>
          </template>
          <b-dropdown-item v-for="option in priorityFilters" :key="'p' + option.value"
                           @click="priorityFilter = option.value">
            <i class="fa fa-check tb-drop__check" :class="{ 'is-hidden': priorityFilter !== option.value }"></i>
            {{ option.text }}
          </b-dropdown-item>
        </b-dropdown>

        <button v-if="hasAnyFilter" type="button" class="tb-chip" @click="resetFilters">
          {{ filterChipText }}
          <i class="fa fa-times"></i>
        </button>

        <span class="tb-spacer"></span>

        <button type="button" class="tb-icon-btn" :class="{ 'is-spin': isLoading }"
                @click="getListTickets" v-b-tooltip.hover.bottom="'Обновить доску'">
          <i class="fa fa-refresh"></i>
        </button>

        <router-link :to="{ name: 'tickets_dashboard' }" class="tb-btn-ghost">
          <i class="fa fa-bar-chart"></i> Дашборд
        </router-link>
      </div>

      <!-- скелетон -->
      <div v-if="isLoading" class="tb-board">
        <section v-for="n in 4" :key="'skcol' + n" class="tb-col">
          <div class="tb-col__head">
            <span class="tb-sk tb-sk--title"></span>
          </div>
          <div class="tb-col__body">
            <span v-for="k in 3" :key="'skc' + k" class="tb-sk tb-sk--card"></span>
          </div>
        </section>
      </div>

      <div v-else-if="!sortedStatuses.length" class="tb-empty">
        <i class="fa fa-columns"></i>
        <div class="tb-empty__title">Статусы не настроены</div>
        <div class="tb-empty__text">Добавьте статусы тикетов — из них собираются колонки доски.</div>
        <router-link :to="{ name: 'tickets_status' }" class="tb-btn-ghost">
          <i class="fa fa-cog"></i> Настроить статусы
        </router-link>
      </div>

      <div v-else ref="board" class="tb-board">
        <section v-for="status in boardColumns" :key="'col' + status.id" class="tb-col">
          <header class="tb-col__head" :class="'tb-col__head--' + status.slug">
            <span class="tb-col__dot"></span>
            <span class="tb-col__name" :title="status.name">{{ status.name }}</span>
            <span class="tb-col__count">{{ columnCount(status) }}</span>
          </header>

          <div class="tb-col__body"
               :class="columnDropClass(status)"
               @scroll.passive="onColumnScroll($event, status)"
               @dragover="onColumnDragOver($event, status)"
               @dragleave="onColumnDragLeave(status)"
               @drop.prevent="onColumnDrop(status)">
            <TicketBoardCardTemplate
                v-for="(ticket, ticketIndex) in ticketsByStatus[status.slug]"
                :key="'t' + status.slug + '-' + (ticket.Id != null ? ticket.Id : 'no-id-' + ticketIndex)"
                :ticket="ticket"
                @open="openSidebar($event, 'chat')"
                @open-history="openSidebar($event, 'history')"
                @open-user="navigateToUser"
                @copy-id="copyTicketId"
                @drag-start="onCardDragStart"
                @drag-end="onCardDragEnd"
            />

            <div v-if="loadingStatuses[status.id]" class="tb-col__more">
              <i class="fa fa-spinner fa-spin"></i> Загружаем карточки…
            </div>

            <div v-else-if="!(ticketsByStatus[status.slug] || []).length" class="tb-col__empty">
              {{ emptyColumnText }}
            </div>

            <div v-else-if="columnHasMore(status) && !hasClientFilter" class="tb-col__hint">
              Прокрутите вниз, чтобы догрузить
            </div>
          </div>
        </section>
      </div>
    </div>
  </TicketLayouts>
</template>

<style scoped lang="scss">
/* ============================ перенос карточек ============================ */
.tb-col__body {
  transition: background .16s ease, box-shadow .16s ease;

  /* колонка, куда перенос возможен */
  &.is-drop-allowed {
    background: var(--tb-primary-soft);
    box-shadow: inset 0 0 0 1px var(--tb-primary);
    border-radius: 12px;
  }

  /* курсор над этой колонкой */
  &.is-drop-over {
    background: var(--tb-primary-soft);
    box-shadow: inset 0 0 0 2px var(--tb-primary);
    border-radius: 12px;
  }

  /* из текущего действия в этот статус перейти нельзя */
  &.is-drop-forbidden { opacity: .45; }
}

.tb-choice {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__text {
    margin: 0 0 12px;
    font-size: 13px;
    color: var(--tb-muted, #7b8496);
  }

  &__btn {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 14px;
    border: 1px solid var(--tb-border, #e7eaf3);
    border-radius: 10px;
    background: var(--tb-bg, #fff);
    color: var(--tb-text, #242934);
    font-size: 13px;
    font-weight: 600;
    text-align: left;
    transition: all .16s ease;

    i { color: var(--tb-primary, #7366ff); }

    &:hover:not(:disabled) {
      border-color: var(--tb-primary, #7366ff);
      background: var(--tb-primary-soft, rgba(115, 102, 255, .1));
    }

    &:disabled { opacity: .6; }
  }
}

/* ============================ токены доски ============================ */
.tb, .sidebar-content-wrapper {
  --tb-bg: #ffffff;
  --tb-soft: #f6f7fb;
  --tb-soft-2: #eef0f7;
  --tb-border: #e7eaf3;
  --tb-text: #242934;
  --tb-muted: #7b8496;
  --tb-primary: var(--theme-deafult, #7366ff);
  --tb-primary-soft: rgba(115, 102, 255, .1);
  --tb-ok: #2fb344;
  --tb-warn: #f0a020;
  --tb-live: #ff7a1a;
  --tb-danger: #e0414f;
  --tb-info: #3aa1e0;
  --tb-shadow: 0 1px 2px rgba(30, 38, 60, .04);
}

body.dark-only .tb, body.dark-only .sidebar-content-wrapper {
  --tb-bg: #262932;
  --tb-soft: #2f3341;
  --tb-soft-2: #363b4b;
  --tb-border: #374558;
  --tb-text: rgba(255, 255, 255, .87);
  --tb-muted: #98a6ad;
  --tb-primary-soft: rgba(115, 102, 255, .2);
}

.tb {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0 4px 4px;
}

/* ============================ тулбар ============================ */
.tb-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: var(--tb-bg);
  border: 1px solid var(--tb-border);
  border-radius: 14px;
  box-shadow: var(--tb-shadow);
  padding: 10px 12px;
}

.tb-spacer { flex: 1 1 auto; }

.tb-search {
  position: relative;
  flex: 1 1 260px;
  max-width: 380px;

  &__icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--tb-muted);
    font-size: 12px;
    pointer-events: none;
  }

  &__input {
    width: 100%;
    height: 38px;
    padding: 0 34px;
    border: 1px solid var(--tb-border);
    border-radius: 10px;
    background: var(--tb-soft);
    color: var(--tb-text);
    font-size: 13px;
    outline: none;

    &::placeholder { color: var(--tb-muted); }

    &:focus {
      background: var(--tb-bg);
      border-color: var(--tb-primary);
      box-shadow: 0 0 0 3px var(--tb-primary-soft);
    }

    &::-webkit-search-cancel-button { display: none; }
  }

  &__clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 22px;
    height: 22px;
    border: 0;
    border-radius: 6px;
    background: var(--tb-soft-2);
    color: var(--tb-muted);
    font-size: 10px;
    cursor: pointer;

    &:hover { background: var(--tb-danger); color: #fff; }
  }
}

.tb-drop {
  ::v-deep > .btn {
    padding: 0;
    border: 0;
    box-shadow: none !important;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 12px;
    border: 1px solid var(--tb-border);
    border-radius: 10px;
    background: var(--tb-soft);
    color: var(--tb-text);
    font-size: 13px;
    font-weight: 500;

    &:hover { border-color: var(--tb-primary); }

    &.is-on {
      border-color: var(--tb-primary);
      background: var(--tb-primary-soft);
      color: var(--tb-primary);
    }
  }

  &__check {
    width: 14px;
    margin-right: 4px;
    color: var(--tb-primary);

    &.is-hidden { visibility: hidden; }
  }

  ::v-deep .tb-drop__menu {
    border: 1px solid var(--tb-border);
    border-radius: 12px;
    padding: 6px;

    .dropdown-item {
      border-radius: 8px;
      font-size: 13px;
      padding: 7px 10px;
    }
  }
}

.tb-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 11px;
  border: 1px solid var(--tb-warn);
  border-radius: 999px;
  background: rgba(240, 160, 32, .12);
  color: #b57512;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.tb-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid var(--tb-border);
  border-radius: 10px;
  background: var(--tb-soft);
  color: var(--tb-muted);
  font-size: 14px;
  cursor: pointer;

  &:hover { border-color: var(--tb-primary); color: var(--tb-primary); }

  &.is-spin i { animation: tb-spin .8s linear infinite; }
}

@keyframes tb-spin { to { transform: rotate(360deg); } }

.tb-btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 14px;
  border: 1px solid var(--tb-border);
  border-radius: 10px;
  background: var(--tb-soft);
  color: var(--tb-text) !important;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none !important;
  cursor: pointer;

  &:hover { border-color: var(--tb-primary); color: var(--tb-primary) !important; }
}

.tb-label {
  display: block;
  margin-bottom: 5px;
  font-size: 12px;
  font-weight: 600;
  color: var(--tb-text);
}

.tb-side-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 12px;
  margin-top: 14px;
}

/* ============================ доска ============================ */
.tb-board {
  display: flex;
  gap: 12px;
  align-items: stretch;
  overflow-x: auto;
  padding-bottom: 6px;
  min-height: 0;
}

.tb-col {
  display: flex;
  flex-direction: column;
  flex: 0 0 300px;
  max-width: 300px;
  background: var(--tb-soft);
  border: 1px solid var(--tb-border);
  border-radius: 14px;
  overflow: hidden;

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 11px 13px;
    background: var(--tb-bg);
    border-bottom: 1px solid var(--tb-border);
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--tb-muted);
    flex: 0 0 auto;
  }

  &__head--new &__dot,
  &__head--classification &__dot { background: var(--tb-info); }
  &__head--in_progress &__dot { background: var(--tb-live); }
  &__head--waiting_response &__dot,
  &__head--reopened &__dot { background: var(--tb-warn); }
  &__head--resolved &__dot,
  &__head--closed &__dot { background: var(--tb-ok); }
  &__head--rejected &__dot { background: var(--tb-danger); }

  &__name {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--tb-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    padding: 1px 8px;
    border-radius: 999px;
    background: var(--tb-soft-2);
    color: var(--tb-muted);
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    overflow-y: auto;
    max-height: calc(100vh - 300px);
    min-height: 200px;

    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: var(--tb-border); border-radius: 3px; }
    &::-webkit-scrollbar-thumb:hover { background: var(--tb-muted); }
  }

  &__more, &__hint, &__empty {
    padding: 12px;
    text-align: center;
    font-size: 12px;
    color: var(--tb-muted);
  }

  &__more {
    border-radius: 10px;
    background: var(--tb-bg);
  }

  &__hint { opacity: .7; font-size: 11px; }
}

.tb-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 60px 20px;
  background: var(--tb-bg);
  border: 1px solid var(--tb-border);
  border-radius: 14px;
  text-align: center;

  > i { font-size: 30px; color: var(--tb-primary); opacity: .6; }

  &__title { font-size: 16px; font-weight: 600; color: var(--tb-text); }
  &__text { font-size: 13px; color: var(--tb-muted); max-width: 420px; }
}

.tb-sk {
  display: block;
  border-radius: 10px;
  background: linear-gradient(90deg, var(--tb-soft) 25%, var(--tb-soft-2) 37%, var(--tb-soft) 63%);
  background-size: 400% 100%;
  animation: tb-shimmer 1.3s ease infinite;

  &--title { height: 14px; width: 60%; }
  &--card { height: 96px; }
}

@keyframes tb-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}


/* ============================ сайдбар, чат и история ============================ */
/* свои индикаторы вкладок: раньше загрузка истории подменяла всю панель */
.tb-side-loading {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 0;

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.tb-sk--dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.tb-sk--line {
  height: 14px;
  border-radius: 7px;
  flex: 1 1 auto;
}

.tb-side-empty {
  margin: 24px 0;
  text-align: center;
  font-size: 12.5px;
  color: var(--tb-muted);
}

.sidebar-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  color: var(--tb-text);
}

.sidebar-top-section {
  flex-shrink: 0;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--tb-border);
}

.sidebar-scroll-section {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
}

/* поля темы, описания и селекты */
.sidebar-top-section ::v-deep {
  .form-control,
  .custom-select {
    height: 38px;
    border: 1px solid var(--tb-border);
    border-radius: 10px;
    background: var(--tb-bg);
    color: var(--tb-text);
    font-size: 13px;
    box-shadow: none;

    &:focus {
      border-color: var(--tb-primary);
      box-shadow: 0 0 0 3px var(--tb-primary-soft);
    }
  }

  .form-group { margin-bottom: 0; }

  .form-group > label {
    margin-bottom: 4px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .05em;
    text-transform: uppercase;
    color: var(--tb-muted);
  }

  .btn-primary {
    height: 34px;
    padding: 0 14px;
    border: 0;
    border-radius: 9px;
    background: var(--tb-primary);
    font-size: 12.5px;
    font-weight: 600;
    box-shadow: none;
  }
}

.tb-label {
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .05em;
  text-transform: uppercase;
  color: var(--tb-muted);
}

.tb-side-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

/* переключатель «Чат / История» */
.tabs {
  display: inline-flex !important;
  gap: 3px;
  padding: 3px;
  margin-top: 14px;
  border: 1px solid var(--tb-border);
  border-radius: 10px;
  background: var(--tb-soft);

  ::v-deep .btn,
  ::v-deep button {
    flex: 1 1 0;
    height: 32px;
    padding: 0 14px;
    border: 0 !important;
    border-radius: 8px !important;
    background: transparent !important;
    color: var(--tb-muted) !important;
    font-size: 12.5px;
    font-weight: 600;
    box-shadow: none !important;

    &:hover { color: var(--tb-primary) !important; }

    &.active {
      background: var(--tb-bg) !important;
      color: var(--tb-primary) !important;
      box-shadow: 0 1px 3px rgba(30, 38, 60, .12) !important;
    }
  }
}

/* ---------- история: лента событий ---------- */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.history-item {
  position: relative;
  display: flex;
  gap: 12px;
  padding: 12px 0 12px 2px;

  /* линия таймлайна между событиями */
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 13px;
    top: 34px;
    bottom: -2px;
    width: 1px;
    background: var(--tb-border);
  }
}

.history-icon {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--tb-primary-soft);
  color: var(--tb-primary);
  font-size: 11px;
  z-index: 1;
}

.history-content {
  min-width: 0;
  flex: 1 1 auto;
}

.history-action {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: var(--tb-muted);

  & + .history-action { margin-top: 4px; }

  span {
    display: inline-flex;
    align-items: center;
    padding: 2px 9px;
    border-radius: 999px;
    background: var(--tb-soft-2);
    color: var(--tb-text);
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
  }
}

.history-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
  font-size: 11.5px;
  color: var(--tb-muted);
}

.history-date { white-space: nowrap; }

/* ---------- чат ---------- */
.chat-messages-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 4px 4px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.message {
  display: flex;
  gap: 10px;
  max-width: 100%;
}

.message-avatar {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--tb-soft-2);
  color: var(--tb-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  transition: background .14s ease, color .14s ease;

  &:hover { background: var(--tb-primary-soft); color: var(--tb-primary); }
}

.message-content {
  min-width: 0;
  flex: 1 1 auto;
}

.message-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
}

.message-author {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--tb-text);
  cursor: pointer;

  &:hover { color: var(--tb-primary); }
}

.message-time {
  font-size: 11px;
  color: var(--tb-muted);
  white-space: nowrap;
}

/* пузырь сообщения */
.message-text {
  display: inline-block;
  max-width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--tb-border);
  border-radius: 12px 12px 12px 4px;
  background: var(--tb-soft);
  color: var(--tb-text);
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

/* свои сообщения — справа и с акцентом */
.message-mine {
  flex-direction: row-reverse;

  .message-avatar { background: var(--tb-primary); color: #fff; }

  .message-header { flex-direction: row-reverse; }

  .message-content { text-align: right; }

  .message-text {
    border-color: var(--tb-primary);
    border-radius: 12px 12px 4px 12px;
    background: var(--tb-primary-soft);
    text-align: left;
  }

  .message-attachments { align-items: flex-end; }
}

.message-attachments {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.attachment-item { max-width: 260px; }

.attachment-image-wrapper {
  position: relative;
  border: 1px solid var(--tb-border);
  border-radius: 12px;
  overflow: hidden;
  background: var(--tb-soft);
}

.attachment-image {
  display: block;
  width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: cover;
  cursor: pointer;
}

.attachment-image-info {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  border-top: 1px solid var(--tb-border);
  background: var(--tb-bg);
}

.attachment-download-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(30, 38, 60, .2);
}

/* файл вложения */
.attachment-file {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 11px;
  border: 1px solid var(--tb-border);
  border-radius: 10px;
  background: var(--tb-bg);
  cursor: pointer;
  transition: all .16s ease;

  &:hover {
    border-color: var(--tb-primary);
    background: var(--tb-primary-soft);
  }
}

.attachment-icon {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: var(--tb-soft-2);
  color: var(--tb-muted);
  font-size: 13px;
}

.attachment-info { min-width: 0; flex: 1 1 auto; }

.attachment-name {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tb-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-size {
  display: block;
  margin-top: 1px;
  font-size: 11px;
  color: var(--tb-muted);
}

.attachment-download {
  flex: 0 0 auto;
  color: var(--tb-primary);
}

.message-attachments.has-multiple-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
}

.message-attachments.has-multiple-images .attachment-image { max-height: 140px; }

/* ---------- поле ввода ---------- */
.chat-input-wrapper {
  flex-shrink: 0;
  padding-top: 12px;
  border-top: 1px solid var(--tb-border);
}

.chat-input {
  display: flex;
  align-items: flex-end;
  gap: 8px;

  ::v-deep textarea.form-control {
    min-height: 40px;
    max-height: 140px;
    padding: 9px 12px;
    border: 1px solid var(--tb-border);
    border-radius: 10px;
    background: var(--tb-bg);
    color: var(--tb-text);
    font-size: 13px;
    line-height: 1.45;
    resize: none;
    box-shadow: none;

    &::placeholder { color: var(--tb-muted); }

    &:focus {
      border-color: var(--tb-primary);
      box-shadow: 0 0 0 3px var(--tb-primary-soft);
    }
  }

  ::v-deep .btn {
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 10px;
    box-shadow: none;
  }

  ::v-deep .attach-btn {
    border: 1px solid var(--tb-border);
    background: var(--tb-bg);
    color: var(--tb-muted);

    &:hover {
      border-color: var(--tb-primary);
      background: var(--tb-primary-soft);
      color: var(--tb-primary);
    }
  }

  ::v-deep .btn-primary {
    border: 0;
    background: var(--tb-primary);
    color: #fff;
  }
}

/* выбранные, но ещё не отправленные файлы */
.attached-files-preview {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}

.attached-file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: 1px solid var(--tb-border);
  border-radius: 9px;
  background: var(--tb-soft);
}

.attached-file-thumb {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  object-fit: cover;
}

.attached-file-icon {
  width: 26px;
  text-align: center;
  color: var(--tb-muted);
}

.attached-file-name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  color: var(--tb-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attached-file-size {
  font-size: 11px;
  color: var(--tb-muted);
  white-space: nowrap;
}

.attached-file-remove {
  padding: 0 4px !important;
  color: var(--tb-muted) !important;

  &:hover { color: var(--tb-danger, #e0414f) !important; }
}


/* Индикатор загрузки для колонок */
:deep(.column-loading-indicator) {
  display: none;
  align-items: center;
  justify-content: center;
  padding: 15px;
  margin: 10px;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 13px;
  color: #667eea;
  gap: 10px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

:deep(.column-loading-indicator .spinner-border) {
  width: 1.2rem;
  height: 1.2rem;
  border-width: 2px;
}
</style>
